import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Copy, Check, Download, Building2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export const SponsorBankDetails = () => {
  const [copied, setCopied] = useState<string | null>(null);
  const { toast } = useToast();

  const bankDetails = {
    beneficiary: "Sands Ltd. (in care of Bobo & Teedee®)",
    bankName: "Cayman National Bank Ltd.",
    accountNumber: "100-123456-001",
    routingNumber: "KY12CYNB0000000000123456",
    swiftCode: "CYNBKYKY",
    address: "George Town, Grand Cayman, Cayman Islands",
    reference: "Bobo & Teedee CNCF Sponsorship"
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      toast({
        title: "Copied!",
        description: `${label} copied to clipboard`,
      });
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to copy to clipboard",
        variant: "destructive",
      });
    }
  };

  const downloadBankDetails = () => {
    const content = `
BOBO & TEEDEE® CORPORATE SPONSORSHIP
BANK TRANSFER DETAILS

Beneficiary: ${bankDetails.beneficiary}
Bank Name: ${bankDetails.bankName}
Account Number: ${bankDetails.accountNumber}
Routing Number: ${bankDetails.routingNumber}
SWIFT Code: ${bankDetails.swiftCode}
Bank Address: ${bankDetails.address}
Payment Reference: ${bankDetails.reference}

IMPORTANT NOTES:
- All donations are processed in care of Sands Ltd.
- Please include "Bobo & Teedee CNCF Sponsorship" in the reference field
- For amounts over KYD 5,000, please notify us in advance
- Tax receipts will be issued by Sands Ltd. upon request

Contact: contact@boboandteedee.com
Phone: +1 (345) 923-5116

Generated: ${new Date().toLocaleDateString()}
    `.trim();

    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bobo-teedee-bank-details.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toast({
      title: "Downloaded!",
      description: "Bank details saved to your device",
    });
  };

  const DetailRow = ({ label, value, copyable = true }: { label: string; value: string; copyable?: boolean }) => (
    <div className="flex items-center justify-between py-3 border-b border-muted last:border-b-0">
      <div className="flex-1">
        <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
        <dd className="text-base font-mono mt-1">{value}</dd>
      </div>
      {copyable && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => copyToClipboard(value, label)}
          className="ml-4 h-8 w-8 p-0"
        >
          {copied === label ? (
            <Check className="h-4 w-4 text-green-500" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </Button>
      )}
    </div>
  );

  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <Building2 className="h-16 w-16 mx-auto mb-6 text-primary" />
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Bank Transfer Details
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Prefer to make a direct bank transfer? Use the details below. 
            All donations are processed securely in care of our registered partner, Sands Ltd.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <Card className="shadow-lg">
            <CardHeader className="text-center bg-gradient-to-r from-primary/10 to-secondary/10">
              <CardTitle className="text-2xl">Wire Transfer Information</CardTitle>
              <p className="text-muted-foreground">
                For sponsorships via bank transfer
              </p>
            </CardHeader>
            
            <CardContent className="p-8">
              <dl className="space-y-0">
                <DetailRow 
                  label="Beneficiary" 
                  value={bankDetails.beneficiary}
                />
                <DetailRow 
                  label="Bank Name" 
                  value={bankDetails.bankName}
                />
                <DetailRow 
                  label="Account Number" 
                  value={bankDetails.accountNumber}
                />
                <DetailRow 
                  label="Routing Number" 
                  value={bankDetails.routingNumber}
                />
                <DetailRow 
                  label="SWIFT Code" 
                  value={bankDetails.swiftCode}
                />
                <DetailRow 
                  label="Bank Address" 
                  value={bankDetails.address}
                  copyable={false}
                />
                <DetailRow 
                  label="Payment Reference" 
                  value={bankDetails.reference}
                />
              </dl>

              <div className="mt-8 pt-6 border-t border-muted">
                <Button 
                  onClick={downloadBankDetails}
                  className="w-full"
                  variant="outline"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download Bank Details
                </Button>
              </div>

              <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                <h4 className="font-semibold mb-2 text-sm">Important Notes:</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Please include the payment reference in your transfer</li>
                  <li>• For amounts over KYD 5,000, notify us in advance</li>
                  <li>• Tax receipts available upon request via Sands Ltd.</li>
                  <li>• Processing time: 2-3 business days</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};